import * as api from '../api';

import {
    createCompany,
    loadCompany,
    updateCompany,
    updateCompanyLicense,
} from './company/company/services';

import { ApiError } from '../ApiError';
import localforage from 'localforage';
import { mutate } from 'swr';

const companyDataFormat = data => {
    return {
        ...data,
        children: [],
        connectors: [],
        devices: 0,
        managed_by: '',
        parent: data.parent.uuid,
        policies: 1,
        subscription: data.subscription_level,
        users: 1,
        tableData: {},
    };
};
export const companiesUrl = '/api/organizations';

export const loadCompanies = async userOrganization => {
    const getData = page => {
        const url = `${companiesUrl}?page=${page}`;
        return api.checkResponse(api.get(url), true);
    };

    try {
        const { data: organizations, total_pages: total } = await getData(1);

        for (let i = 2; i <= total; i++) {
            const { data } = await getData(i);
            organizations.push(...data);
        }
        return buildResults(organizations, userOrganization);
    } catch (error) {
        console.error('Error loading companies:', error);
        return [];
    }
};

const buildResults = (items, userOrganization) => {
    const tree = buildParentChild(items);
    const results = [];

    tree.forEach(item => {
        if (userOrganization && item.uuid === userOrganization) {
            const children = item.children;
            item.children = [];
            results.push(item);
            results.push(...children);
        } else {
            results.push(item);
        }
    });

    return results;
};

const buildParentChild = items => {
    const groupByParents = new Map();
    const uuidToItem = new Map(
        items.map(item => [item.uuid, { ...item, children: [] }])
    );
    const parents = new Set();

    items.forEach(item => {
        if (item.parent && uuidToItem.has(item.parent)) {
            if (!groupByParents.has(item.parent)) {
                groupByParents.set(item.parent, []);
            }
            groupByParents.get(item.parent).push(item.uuid);
        } else {
            parents.add(item.uuid);
        }
    });

    const buildChildren = uuid => {
        const item = uuidToItem.get(uuid);
        const children = groupByParents.get(uuid) || [];
        item.children = children.map(buildChildren);
        return item;
    };

    return [...parents].map(buildChildren);
};

export const addCompany = async (currentUserUuid, data) => {
    const cacheKey = currentUserUuid + '-' + companiesUrl;
    try {
        const newCompany = await createCompany(data);
        const newCompanyData = companyDataFormat(await loadCompany(newCompany));
        mutate(
            cacheKey,
            existingCompanies => [...(existingCompanies || []), newCompanyData],
            false
        );

        const storedData = (await localforage.getItem(cacheKey)) || [];
        await localforage.setItem(cacheKey, [
            ...(storedData || []),
            newCompanyData,
        ]);
        return newCompany;
    } catch (error) {
        console.error('Error add new company:', error.message);
        return { error: error.message, detail: error.detail, id: error.id };
    }
};

export const migrateCompanies = async uuids => {
    const data = uuids.map(uuid => ({ organization_uuid: uuid }));
    try {
        return await api.checkResponse(
            api.post('/api/organization/migration', data)
        );
    } catch (error) {
        console.error('Error migrating companies:', error.message);
        throw { error: error.message, detail: error.detail, id: error.id };
    }
};

export const deleteCompanies = async (currentUserUuid, uuids) => {
    const cacheKey = currentUserUuid + '-' + companiesUrl;

    const filterData = list => {
        return list
            .map(item => {
                if (item.children) {
                    item.children = filterData(item.children);
                }
                return item;
            })
            .filter(item => !uuids.includes(item.uuid));
    };

    try {
        const response = await api.checkResponse(
            api.remove(companiesUrl, { uuids })
        );

        mutate(
            cacheKey,
            currentData =>
                currentData ? filterData(currentData) : currentData,
            false
        );

        const storedData = (await localforage.getItem(cacheKey)) || [];
        await localforage.setItem(cacheKey, filterData(storedData));

        return response;
    } catch (error) {
        console.error('Error deleting companies:', error.message);
        throw { error: error.message, detail: error.detail, id: error.id };
    }
};

export const modifyCompany = async (currentUserUuid, method, uuid, data) => {
    try {
        const cacheKey = `${currentUserUuid}-${companiesUrl}`;
        let response = null;

        switch (method) {
            case 'UPDATE_LICENSE':
                response = await updateCompanyLicense(uuid, data);
                break;
            default:
                response = await updateCompany(uuid, data, Boolean(data.logo));
                break;
        }

        const companyData = companyDataFormat(await loadCompany(uuid));

        const recurseUpdate = item => {
            if (item.uuid === companyData.uuid) {
                Object.assign(item, companyData);
                return true;
            }
            for (const child of item.children) {
                if (recurseUpdate(child)) {
                    return true;
                }
            }
            return false;
        };

        const updateState = currentData => {
            if (!currentData) return currentData;
            const newState = [...currentData];
            for (const item of newState) {
                const didUpdate = recurseUpdate(item);
                if (didUpdate) {
                    break;
                }
            }
            return newState;
        };

        mutate(cacheKey, updateState, false);

        const storedData = (await localforage.getItem(cacheKey)) || {};
        const newStoredDataVersion = updateState(storedData);

        await localforage.setItem(cacheKey, newStoredDataVersion);

        return response;
    } catch (error) {
        console.error('Error: handlePatch fetchers error:', error);
        return { error: error.message, detail: error.detail, id: error.id };
    }
};

export const loadCompanyUsers = companyId =>
    api.checkResponse(api.get(`/api/organization/${companyId}/users`));

export const usersFetchers = async (
    currentUserUuid,
    companyId,
    data,
    method,
    fetch
) => {
    const usersUrl = `/api/organization/${companyId}/users`;
    const cacheKey = currentUserUuid + '-' + usersUrl;
    switch (method) {
        case 'POST':
            return await handlePost(cacheKey, companyId, data, fetch);
        case 'PATCH':
            return await handlePatch(cacheKey, companyId, data, fetch);
        case 'REMOVE':
            return await handleRemove(cacheKey, companyId, data, fetch);
        default:
            throw new Error('Method not supported');
    }
};

const handlePost = async (cacheKey, companyId, data, fetch) => {
    try {
        const response = await api.checkResponse(fetch);
        if (Array.isArray(response)) {
            const userResponses = await Promise.all(
                response.map(user =>
                    api.get(
                        `/api/organization/${companyId}/users?invitation_uuid=${user.uuid}`
                    )
                )
            );

            const usersData = userResponses.flatMap(userResponse => {
                const { users } = userResponse;
                return users;
            });

            await new Promise(resolve => setTimeout(resolve, 2000));

            mutate(
                cacheKey,
                currentData => {
                    const existingUsers = currentData?.users || [];
                    const existingStats = currentData?.stats || {};

                    return {
                        users: [...existingUsers, ...usersData],
                        stats: {
                            ...existingStats,
                            account_count:
                                (existingStats.account_count || 0) +
                                usersData.length,
                            remaining_seats:
                                (existingStats.remaining_seats || 0) -
                                usersData.length,
                        },
                    };
                },
                true
            );

            const storedData = (await localforage.getItem(cacheKey)) || {
                users: [],
                stats: {},
            };

            await localforage.setItem(cacheKey, {
                users: [...(storedData?.users || []), ...usersData],
                stats: {
                    ...storedData.stats,
                    account_count:
                        (storedData.stats.account_count || 0) +
                        usersData.length,
                    remaining_seats:
                        (storedData.stats.remaining_seats || 0) -
                        usersData.length,
                },
            });
        }

        return response;
    } catch (error) {
        if (error instanceof ApiError) {
            console.error('Error: handlePost fetchers error:', error.message);
            return {
                error: error.message,
                detail: error.detail,
                id: error.id,
            };
        } else {
            console.error('Error: handlePost fetchers error:', error.message);
            return {
                error: 'Error trying to create user',
                detail: error.detail,
                id: error.id,
            };
        }
    }
};

const handlePatch = async (cacheKey, companyId, data, fetch) => {
    try {
        const response = await api.checkResponse(fetch);
        const accounts = data.accounts;

        const accountResponses = await Promise.all(
            accounts.map(account =>
                api.checkResponse(
                    api.get(
                        `/api/organization/${companyId}/users?account_uuid=${account.uuid}`
                    )
                )
            )
        );

        const userMap = accountResponses.reduce((acc, accountResponse) => {
            accountResponse.users.forEach(user => {
                acc[user.uuid] = user;
            });
            return acc;
        }, {});

        mutate(
            cacheKey,
            currentData => {
                if (!currentData) return currentData;

                const updatedUsers = currentData.users.map(user => {
                    const updatedUser = userMap[user.uuid];
                    return updatedUser ? { ...user, ...updatedUser } : user;
                });

                return { ...currentData, users: updatedUsers };
            },
            false
        );

        const storedData = (await localforage.getItem(cacheKey)) || {
            users: [],
            stats: {},
        };
        const updatedUsers = storedData.users.map(user => {
            const updatedUser = userMap[user.uuid];
            return updatedUser ? { ...user, ...updatedUser } : user;
        });

        await localforage.setItem(cacheKey, {
            ...storedData,
            users: updatedUsers,
        });

        return response;
    } catch (error) {
        console.error('Error: handlePatch fetchers error:', error);
        return { error: error.message, detail: error.detail, id: error.id };
    }
};

const handleRemove = async (cacheKey, companyId, data, fetch) => {
    const invitations = data.invitations || [];
    const filterData = list => {
        return list.filter(
            item =>
                !data.accounts.some(d => d.uuid === item.uuid) &&
                !data.accounts.some(
                    d => d.uuid === item.organization_account_uuid
                ) &&
                !invitations.some(d => d.uuid === item.uuid)
        );
    };

    mutate(
        cacheKey,
        currentData => {
            const updatedUsers = filterData(currentData?.users || []);
            const updatedAccountCount =
                (currentData?.stats?.account_count || 0) - data.accounts.length;
            const updatedRemainingSeats =
                (currentData?.stats?.remaining_seats || 0) +
                data.accounts.length;

            return {
                ...currentData,
                users: updatedUsers,
                stats: {
                    ...currentData.stats,
                    account_count: updatedAccountCount,
                    remaining_seats: updatedRemainingSeats,
                    pending_approval_count:
                        (currentData?.stats?.pending_approval_count || 0) -
                        invitations.length,
                },
            };
        },
        false
    );

    const storedData = (await localforage.getItem(cacheKey)) || {
        users: [],
        stats: {},
    };

    await localforage.setItem(cacheKey, {
        ...storedData,
        users: filterData(storedData.users || []),
        stats: {
            ...storedData.stats,
            account_count:
                (storedData.stats?.account_count || 0) - data.accounts.length,
            remaining_seats:
                (storedData.stats?.remaining_seats || 0) + data.accounts.length,
            pending_approval_count:
                (storedData.stats?.pending_approval_count || 0) -
                invitations.length,
        },
    });

    try {
        const response = await api.checkResponse(fetch);
        return response;
    } catch (error) {
        console.error('Error: handleRemove fetchers error:', error);
        mutate(cacheKey);
        return { error: error.message, detail: error.detail, id: error.id };
    }
};

export const handleDisableUser2FA = async (
    cacheKey,
    companyId,
    data,
    fetch
) => {
    try {
        const response = await api.checkResponse(fetch);

        const updateUser = u =>
            u.uuid === data.uuid
                ? { ...u, multi_factor_authentication: false }
                : u;

        mutate(
            cacheKey,
            currentData => ({
                ...currentData,
                users: (currentData?.users || []).map(updateUser),
            }),
            false
        );

        const storedData = (await localforage.getItem(cacheKey)) || {
            users: [],
            stats: {},
        };
        await localforage.setItem(cacheKey, {
            ...storedData,
            users: (storedData?.users || []).map(updateUser),
        });

        return response;
    } catch (error) {
        console.error('Error: handleRemove fetchers error:', error);
        return { error: error.message, detail: error.detail, id: error.id };
    }
};

export const handlePrimaryAccount = async (
    currentUserUuid,
    companyId,
    data,
    fetch
) => {
    const usersUrl = `/api/organization/${companyId}/users`;
    const cacheKey = currentUserUuid + '-' + usersUrl;
    try {
        const response = await api.checkResponse(fetch);

        const updatePrimary = users =>
            users.map(u => ({
                ...u,
                is_primary_account: u.uuid === data.primary_account,
            }));

        mutate(
            cacheKey,
            currentData => {
                if (!currentData) return currentData;
                return {
                    ...currentData,
                    users: updatePrimary(currentData?.users || []),
                };
            },
            false
        );

        const storedData = (await localforage.getItem(cacheKey)) || {
            users: [],
            stats: {},
        };

        await localforage.setItem(cacheKey, {
            ...storedData,
            users: updatePrimary(storedData.users || []),
        });

        return response;
    } catch (error) {
        console.error('Error: Transfer primary account:', error);
        return { error: error.message, detail: error.detail, id: error.id };
    }
};
